import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "../config";
import type {
  DiseaseTaxonomyNode,
  DiseaseTaxonomyRoot,
} from "../types/diseaseTaxonomy.types";
import {
  getCachedRoots,
  setCachedRoots,
  getCachedTaxonomy,
  setCachedTaxonomy,
} from "../services/diseaseTaxonomyCache";


async function fetchRootsWithCache(): Promise<DiseaseTaxonomyRoot[]> {
  const cached = await getCachedRoots();
  if (cached) return cached;

  const res = await fetch(`${API_BASE_URL}/api/mondoDisease/getRoots`);
  if (!res.ok) throw new Error("Failed to fetch MONDO roots");
  const data: DiseaseTaxonomyRoot[] = await res.json();
  await setCachedRoots(data);
  return data;
}

async function fetchTaxonomyWithCache(
  rootMondoId: string
): Promise<DiseaseTaxonomyNode[]> {
  const cached = await getCachedTaxonomy(rootMondoId);
  if (cached) return cached;

  const res = await fetch(
    `${API_BASE_URL}/api/mondoDisease/getTaxonomy/${encodeURIComponent(rootMondoId)}`
  );
  if (!res.ok) throw new Error(`Failed to fetch taxonomy for ${rootMondoId}`);
  const data: DiseaseTaxonomyNode[] = await res.json();
  await setCachedTaxonomy(rootMondoId, data);
  return data;
}

export function useDiseaseTaxonomyRoots() {
  const queryClient = useQueryClient();
  const prefetchedRef = useRef(false);

  const query = useQuery<DiseaseTaxonomyRoot[]>({
    queryKey: ["disease-taxonomy-roots"],
    queryFn: fetchRootsWithCache,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!query.data || prefetchedRef.current) return;
    prefetchedRef.current = true;

    for (const root of query.data) {
      queryClient.prefetchQuery({
        queryKey: ["disease-taxonomy", root.mondo_id],
        queryFn: () => fetchTaxonomyWithCache(root.mondo_id),
        staleTime: 0,
      });
    }
  }, [query.data, queryClient]);

  return query;
}

export function useDiseaseTaxonomy(rootMondoId: string | undefined) {
  return useQuery<DiseaseTaxonomyNode[]>({
    queryKey: ["disease-taxonomy", rootMondoId],
    queryFn: () => fetchTaxonomyWithCache(rootMondoId!),
    enabled: !!rootMondoId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}
